/**
 * BudgetVsActualCard — Dashboard widget that answers "are we on track?" in
 * one glance: a horizontal stacked progress bar of the total budget (paid
 * slice + committed-but-unpaid slice + remaining), plus a tiny sparkline
 * overlaying the linear target pacing against what's actually been paid
 * month-over-month. Self-hides only when the wedding has a $0 budget set.
 *
 * Props: { data: budget_trajectory from dashboard response, currency }
 */
import React, { useMemo } from 'react';
import { Card } from '../ui/card';
import { GraphUp, ArrowRight, WarningTriangle } from 'iconoir-react';
import { formatCurrency } from './constants';

export default function BudgetVsActualCard({ data, currency }) {
  const total = data?.total_budget || 0;
  const paid = data?.spent_actual || 0;
  const committed = data?.committed || 0;
  const over = data?.over_budget;
  const series = data?.monthly_series || [];

  const paidPct = useMemo(() => (total > 0 ? Math.min(100, (paid / total) * 100) : 0), [paid, total]);
  const committedPct = useMemo(() => {
    if (total <= 0) return 0;
    const scheduledUnpaid = Math.max(0, committed - paid);
    return Math.min(100 - paidPct, (scheduledUnpaid / total) * 100);
  }, [committed, paid, total, paidPct]);
  const remainingPct = Math.max(0, 100 - paidPct - committedPct);

  // Sparkline: scale all y values to the max between target and actual peaks
  const spark = useMemo(() => {
    if (series.length === 0) return { width: 0, height: 40, targetD: '', actualD: '' };
    const w = 100;
    const h = 40;
    const peak = Math.max(
      1,
      ...series.flatMap((m) => [m.target || 0, m.actual || 0]),
    );
    const x = (i) => (series.length === 1 ? w : (i / (series.length - 1)) * w);
    const y = (v) => h - ((v || 0) / peak) * h;
    const toD = (key) => series.map((m, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(2)} ${y(m[key]).toFixed(2)}`).join(' ');
    return { width: w, height: h, targetD: toD('target'), actualD: toD('actual') };
  }, [series]);

  if (total <= 0) return null;

  return (
    <Card className="card-surface p-6" data-testid="budget-vs-actual-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <GraphUp className="w-5 h-5 text-muted" strokeWidth={1.5} />
          <h2 className="font-serif text-xl text-charcoal">Budget vs Actual</h2>
        </div>
        <a href="/expenses" className="text-terracotta hover:text-terracotta-dark text-xs flex items-center gap-1">
          Budget details <ArrowRight className="w-3 h-3" />
        </a>
      </div>

      {/* Over-budget banner */}
      {over && (
        <div className="flex items-center gap-2 px-3 py-2 mb-3 bg-terracotta/5 border border-terracotta/30 rounded-sm">
          <WarningTriangle className="w-4 h-4 text-terracotta shrink-0" />
          <span className="text-xs text-terracotta">
            Committed spending is {formatCurrency(committed - total, currency)} over your budget.
          </span>
        </div>
      )}

      {/* Stacked progress bar */}
      <div className="h-3 rounded-full overflow-hidden bg-border flex mb-2" data-testid="budget-bar">
        <div
          className="h-full bg-moss transition-all duration-500"
          style={{ width: `${paidPct}%` }}
          title={`Paid: ${formatCurrency(paid, currency)}`}
          data-testid="budget-bar-paid"
        />
        <div
          className="h-full bg-ochre transition-all duration-500"
          style={{ width: `${committedPct}%` }}
          title={`Committed but unpaid: ${formatCurrency(Math.max(0, committed - paid), currency)}`}
          data-testid="budget-bar-committed"
        />
        {remainingPct > 0 && (
          <div
            className="h-full bg-border/60 transition-all duration-500"
            style={{ width: `${remainingPct}%` }}
            title={`Remaining: ${formatCurrency(total - committed, currency)}`}
          />
        )}
      </div>

      {/* Legend row with big numbers */}
      <div className="grid grid-cols-3 gap-3 mt-4">
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <span className="w-2 h-2 rounded-full bg-moss inline-block" />
            <span className="text-[10px] uppercase tracking-widest text-muted">Paid</span>
          </div>
          <p className="font-mono text-lg text-charcoal tracking-tight" data-testid="budget-paid-amount">
            {formatCurrency(paid, currency)}
          </p>
        </div>
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <span className="w-2 h-2 rounded-full bg-ochre inline-block" />
            <span className="text-[10px] uppercase tracking-widest text-muted">Scheduled</span>
          </div>
          <p className="font-mono text-lg text-charcoal tracking-tight" data-testid="budget-committed-amount">
            {formatCurrency(Math.max(0, committed - paid), currency)}
          </p>
        </div>
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <span className="w-2 h-2 rounded-full bg-border/80 inline-block" />
            <span className="text-[10px] uppercase tracking-widest text-muted">
              {over ? 'Over budget' : 'Remaining'}
            </span>
          </div>
          <p className={`font-mono text-lg tracking-tight ${over ? 'text-terracotta' : 'text-charcoal'}`}
            data-testid="budget-remaining-amount">
            {formatCurrency(over ? committed - total : total - committed, currency)}
          </p>
        </div>
      </div>

      {/* Sparkline: target (dashed grey) vs actual (terracotta) */}
      {series.length >= 2 && (
        <div className="mt-5 pt-4 border-t border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-widest text-muted">Pacing</span>
            <div className="flex items-center gap-3 text-[10px] text-muted">
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 border-t border-dashed border-muted" /> target
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 border-t-2 border-terracotta" /> actual
              </span>
            </div>
          </div>
          <svg
            viewBox={`0 0 ${spark.width} ${spark.height}`}
            preserveAspectRatio="none"
            className="w-full h-12"
            data-testid="budget-sparkline"
          >
            <path d={spark.targetD} stroke="#8B7B7D" strokeWidth="0.5"
              strokeDasharray="2 2" fill="none" opacity="0.7" />
            <path d={spark.actualD} stroke="#D1B0AB" strokeWidth="1" fill="none" />
          </svg>
          <div className="flex items-center justify-between mt-1 text-[10px] text-muted">
            <span>{series[0].label}</span>
            <span>{series[series.length - 1].label}</span>
          </div>
        </div>
      )}
    </Card>
  );
}
